import { useContext, useEffect, useMemo, useState } from "react";
import { planningUserTaskService } from "../../../../Core/Api/services";
import { Button, Chip, Grid, Grid2, InputAdornment, OutlinedInput, Stack, Typography } from "@mui/material";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import EmojiPeopleOutlinedIcon from "@mui/icons-material/EmojiPeopleOutlined";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../../Components/Misc/ListPageHeader";
import PageFilter from "../../../../Components/Misc/PageFilter";
import { DbOrderManualTaskTemplateInfos } from "../../../../Core/Api/Types/db-order-manual-task-template-infos";
import { DbOrderManualTaskServiceInfos } from "../../../../Core/Api/Types/db-order-manual-task-service-infos";
import { DbOrderManualTaskTasksInfos } from "../../../../Core/Api/Types/db-order-manual-task-tasks-infos";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid";
import TableCellDateTime from "../../../../Components/Table/TableCellDateTime/table-cell-date-time";
import TableCellDescription from "../../../../Components/Table/TableCellDescription";
import TableCellBool from "../../../../Components/Table/TableCellBool";
import CallSplitOutlinedIcon from "@mui/icons-material/CallSplitOutlined";
import BuildIcon from "@mui/icons-material/Build";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import AddToPhotosOutlinedIcon from "@mui/icons-material/AddToPhotosOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { GetEnumValueLabelMap, UserHasAcl } from "../../../../Utils/helpers";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import { useNavigate } from "react-router";
import { routePaths } from "../../../../Router/router";
import ModalServiceManager, {
    ModalServiceManagerElementProps,
} from "../../../../Components/Modals/ModalServiceManager/modal-service-manager";
import ModalTaskManager, {
    ModalTaskManagerElementProps,
} from "../../../../Components/Modals/ModalTaskManager/modal-task-manager";
import { ModalContext } from "../../../../Components/Modals";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { produce } from "immer";
import ModalConfirmAction, {
    ModalConfirmActionElementProps,
} from "../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import { useTranslation } from "react-i18next";
import { OrderProductType } from "../../../../Core/Api/Types/order-product-type";
import TableUuids from "../../../../Components/Table/table-uuids";

function AdminSettingsManualTasks() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modalContext = useContext(ModalContext);
    const { openModal } = { ...modalContext };
    const enqueueCustomSnackbar = useCustomSnackbar();

    const [isLoading, setIsLoading] = useState(false);

    const [templates, setTemplates] = useState<DbOrderManualTaskTemplateInfos[]>([]);
    const [services, setServices] = useState<DbOrderManualTaskServiceInfos[]>([]);
    const [tasks, setTasks] = useState<DbOrderManualTaskTasksInfos[]>([]);
    const [trigger, setTrigger] = useState<boolean>(false);
    const [selectedConfig, setSelectedConfig] = useState<"Templates" | "Services" | "Tasks">("Templates");

    const handleConfigTemplates = (id) => {
        navigate(routePaths.settingsManualTasksTemplateConfig.path + "/" + id);
    };

    const handleClone = (data, context) => {
        switch (context) {
            case "Template":
                let tempRequest = data;
                tempRequest.description = "Copy of " + data.description;

                planningUserTaskService.CreateManualTasksTemplate(tempRequest).then((response) => {
                    if (response.success) {
                        enqueueCustomSnackbar(t("alert:success.templateClonedAndNewTemplateCreated"), "success");
                        setTrigger((prevState) => !prevState);
                    } else enqueueCustomSnackbar(response.message!, "error");
                });
                break;
            case "Task":
                let taskRequest = data;
                taskRequest.name = "Copy of " + data.name;

                planningUserTaskService.CreateManualTasksTask(taskRequest).then((response) => {
                    if (response.success) {
                        setTrigger((prevState) => !prevState);
                        enqueueCustomSnackbar(t("alert:success.templateCloned"), "success");
                    } else enqueueCustomSnackbar(response.message!, "error");
                });
        }
    };

    const handleDeleteTemplate = (id) => {
        if (!openModal) return;

        openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
            displayComment: true,
            commentLabel: t("modal:comment.confirmDeleteTemplate"),
            title: t("modal:title.deleteTemplate"),
            onConfirm: () => {
                planningUserTaskService.DeleteManualTasksTemplate(id).then((response) => {
                    if (response.success) {
                        setTemplates(
                            produce((draft) => {
                                let index = draft.findIndex((x) => x.id === id);
                                draft.splice(index, 1);
                            })
                        );
                        enqueueCustomSnackbar(t("alert:success.templateDeleted"), "success");
                    } else enqueueCustomSnackbar(response.message!, "error");
                });
            },
        });
    };

    useEffect(() => {
        setIsLoading(true);
        planningUserTaskService.GetManualTasksTemplates().then((response) => {
            if (response.templates) {
                setTemplates(response.templates);
                setIsLoading(false);
            }
        });
        planningUserTaskService.GetManualTasksTasksGroupedByServices().then((response) => {
            let services: DbOrderManualTaskServiceInfos[] = [];
            let tasks: DbOrderManualTaskTasksInfos[] = [];

            if (response.tasks) {
                response.tasks.forEach((task) => {
                    if (task.service) {
                        services.push(task.service);
                    }
                    if (task.tasks && task.tasks?.length > 0) {
                        task.tasks.forEach((tt) => {
                            tt.serviceName = task.service?.name;
                            tasks.push(tt);
                        });
                    }
                });
            }
            setServices(services);
            setTasks(tasks);
            setIsLoading(false);
        });
    }, [trigger]);

    const templatesColumns: GridColDef[] = [
        {
            field: "id",
            headerName: "ID",
            flex: CellSize.xsmall,
            minWidth: CellSize.xsmall,
        },
        {
            field: "description",
            headerName: t("common:description"),
            flex: CellSize.large,
            minWidth: CellSize.large,
            renderCell: (data: any) => <Typography>{data.value}</Typography>,
        },
        {
            field: "createdDate",
            headerName: t("common:creationDateLowerCase"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
            renderCell: (data: any) => <TableCellDateTime data={data} />,
        },
        {
            field: "group",
            headerName: t("common:group"),
            flex: CellSize.large,
            minWidth: CellSize.large,
            renderCell: (data: any) => <Typography>{data.value ? data.value : "-"}</Typography>,
        },
        {
            field: "productType",
            headerName: t("table:headerName.associatedProductType"),
            type: "singleSelect",
            valueOptions: GetEnumValueLabelMap(OrderProductType),
            flex: CellSize.xlarge,
            minWidth: CellSize.xlarge,
            renderCell: (data: any) => <Chip label={data.value} size="small" />,
        },
        {
            field: "isSystem",
            headerName: t("table:headerName.isSystem"),
            type: "boolean",
            flex: CellSize.xsmall,
            minWidth: CellSize.xsmall,
            renderCell: (data: any) => <TableCellBool value={data.value} />,
        },
        {
            field: "isInternal",
            headerName: t("table:headerName.isInternal"),
            type: "boolean",
            flex: CellSize.xsmall,
            minWidth: CellSize.xsmall,
            renderCell: (data: any) => <TableCellBool value={data.value} />,
        },
        {
            field: "erpHeaderInfos",
            headerName: t("table:headerName.erpManaged"),
            type: "boolean",
            flex: CellSize.xsmall,
            minWidth: CellSize.xsmall,
            renderCell: (data: any) => <TableCellBool value={data.value !== null} />,
        },
        {
            field: "pricesSum",
            headerName: t("common:price"),
            flex: CellSize.large,
            minWidth: CellSize.large,
            valueGetter: (data: any, row: any) => row?.price?.map((x) => `${x.price || ""} ${x.currency || ""}`).join(","),
            renderCell: (data: any) =>
                data?.row?.pricesSum ? (
                    <Stack sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} direction={"row"}>
                        {data?.row?.pricesSum.map((x, index) => (
                            <Chip label={x.price + " " + x.currency} key={index} color="secondary" size="small" />
                        ))}
                    </Stack>
                ) : (
                    ""
                ),
        },
        {
            field: "accountManagerEmail",
            headerName: t("table:headerName.erpAccountManager"),
            flex: CellSize.large,
            minWidth: CellSize.large,
            renderCell: (data: any) => (
                <Typography>
                    {data?.row?.erpHeaderInfos !== null ? data?.row?.erpHeaderInfos.accountManagerEmail : "-"}
                </Typography>
            ),
        },
        {
            field: "clientId",
            headerName: t("table:headerName.expClientId"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: any) => (
                <Typography>{data?.row?.erpHeaderInfos !== null ? data?.row?.erpHeaderInfos.clientId : "-"}</Typography>
            ),
        },
        {
            field: "projectName",
            headerName: t("table:headerName.erpProjectName"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: any) => (
                <Typography>{data?.row?.erpHeaderInfos !== null ? data?.row?.erpHeaderInfos.projectName : "-"}</Typography>
            ),
        },
        {
            field: "actions",
            type: "actions",
            align: "right",
            flex: CellSize.actionsSmall,
            minWidth: CellSize.actionsSmall,
            getActions: (params: GridRowParams) => {
                if (!params || !params.row) return [];
                let actions = [
                    <GridActionsCellItem
                        icon={<BuildIcon sx={{ width: 15, height: 15, color: "text.primary" }} />}
                        label={t("table:cellLabel.configure")}
                        showInMenu
                        disabled={(params?.row?.isInternal && !UserHasAcl(AclTypes.aclSuperUser)) || params?.row?.isSystem}
                        onClick={() => handleConfigTemplates(params?.row?.id)}
                    />,
                ];

                const cloneAction = (
                    <GridActionsCellItem
                        icon={<AddToPhotosOutlinedIcon sx={{ width: 15, height: 15, color: "text.primary" }} />}
                        label={t("table:cellLabel.clone")}
                        showInMenu
                        disabled={params?.row?.isInternal}
                        onClick={() => handleClone(params.row, "Template")}
                    />
                );

                const deleteAction = (
                    <GridActionsCellItem
                        icon={<DeleteIcon sx={{ width: 15, height: 15, color: "text.error" }} />}
                        label={t("common:delete")}
                        showInMenu
                        onClick={() => handleDeleteTemplate(params?.row?.id)}
                    />
                );

                if (UserHasAcl(AclTypes.aclBackofficeManualTasksCreate)) actions.push(cloneAction);
                if (UserHasAcl(AclTypes.aclBackofficeManualTasksDelete)) actions.push(deleteAction);

                return actions;
            },
        },
    ];

    const servicesColumns: GridColDef[] = [
        {
            field: "id",
            headerName: "ID",
            flex: CellSize.xsmall,
            minWidth: CellSize.xsmall,
        },
        {
            field: "name",
            headerName: t("common:name"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
            renderCell: (data: any) => <Typography>{data.value ? data.value : "-"}</Typography>,
        },
        {
            field: "description",
            headerName: t("common:description"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
            renderCell: (data: any) => <Typography color="text.secondary">{data.value}</Typography>,
        },
        {
            field: "managed",
            headerName: t("table:headerName.erpManagedQuestionMark"),
            flex: CellSize.xsmall,
            minWidth: CellSize.xsmall,
            type: "boolean",
            renderCell: (data: any) => <TableCellBool value={data?.row?.erpCode !== null} />,
        },
        {
            field: "erpCode",
            headerName: t("table:headerName.erpCode"),
            flex: CellSize.large,
            minWidth: CellSize.large,
            type: "boolean",
            renderCell: (data: any) => <Typography>{data.value ? data.value : "-"}</Typography>,
        },
        {
            field: "actions",
            type: "actions",
            align: "right",
            flex: CellSize.actionsSmall,
            minWidth: CellSize.actionsSmall,
            getActions: (params: GridRowParams) => [
                <GridActionsCellItem
                    icon={<BuildIcon sx={{ width: 15, height: 15, color: "text.primary" }} />}
                    label={t("table:cellLabel.configure")}
                    showInMenu
                    disabled={params?.row?.erpCode !== null}
                    onClick={() => {
                        if (!openModal) return;

                        openModal<ModalServiceManagerElementProps>(ModalServiceManager, {
                            service: params.row,
                            context: "Update",
                            onConfirm: () => {
                                setTrigger((prevState) => !prevState);
                            },
                        });
                    }}
                />,
            ],
        },
    ];

    const tasksColumns: GridColDef[] = [
        {
            field: "id",
            headerName: "ID",
            flex: CellSize.xsmall,
            minWidth: CellSize.xsmall,
        },
        {
            field: "name",
            headerName: t("common:name"),
            flex: CellSize.large,
            minWidth: CellSize.large,
            renderCell: (data: any) => <Typography>{data.value ? data.value : "-"}</Typography>,
        },
        {
            field: "serviceName",
            headerName: t("common:serviceName"),
            flex: CellSize.large,
            minWidth: CellSize.large,
            renderCell: (data: any) => <Typography>{data.value ? data.value : "-"}</Typography>,
        },
        {
            field: "description",
            headerName: t("common:description"),
            flex: CellSize.large,
            minWidth: CellSize.large,
            renderCell: (data: any) => <Typography color="text.secondary">{data.value}</Typography>,
        },
        {
            field: "comment",
            headerName: t("common:comment"),
            flex: CellSize.large,
            minWidth: CellSize.large,
            renderCell: (data: any) => <Typography>{data.value ? data.value : "-"}</Typography>,
        },
        {
            field: "managedErp",
            headerName: t("table:headerName.erpManagedQuestionMark"),
            type: "boolean",
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: any) => <TableCellBool value={data?.row?.erpTaskCode !== null} />,
        },
        {
            field: "erpTaskCode",
            headerName: t("table:headerName.erpCodeUpperCase"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: any) => <Typography>{data.value ? data.value : "-"}</Typography>,
        },
        {
            field: "pricesSum",
            headerName: t("common:price"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            valueGetter: (data: any, row: any) => row?.price?.map((x) => `${x.price || ""} ${x.currency || ""}`).join(","),
            renderCell: (data: any) =>
                data?.row?.prices ? (
                    <Stack sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} direction={"row"}>
                        {data?.row?.prices.map((x, index) => (
                            <Chip label={x.price + " " + x.currency} key={index} color="secondary" size="small" />
                        ))}
                    </Stack>
                ) : (
                    ""
                ),
        },
        {
            field: "actions",
            type: "actions",
            align: "right",
            getActions: (params: GridRowParams) => [
                <GridActionsCellItem
                    icon={<BuildIcon sx={{ width: 15, height: 15, color: "text.primary" }} />}
                    label={t("table:cellLabel.configure")}
                    showInMenu
                    disabled={params?.row?.erpTaskCode !== null && !UserHasAcl(AclTypes.aclSuperUser)}
                    onClick={() => {
                        if (!openModal) return;

                        openModal<ModalTaskManagerElementProps>(ModalTaskManager, {
                            task: params.row,
                            context: "Update",
                            services: services,
                            onConfirm: () => {
                                setTrigger((prevState) => !prevState);
                            },
                        });
                    }}
                />,
                <GridActionsCellItem
                    icon={<AddToPhotosOutlinedIcon sx={{ width: 15, height: 15, color: "text.primary" }} />}
                    label={t("table:cellLabel.clone")}
                    color="error"
                    showInMenu
                    onClick={() => handleClone(params.row, "Task")}
                />,
            ],
        },
    ];

    const additionalActions = useMemo(() => {
        switch (selectedConfig) {
            case "Templates":
                return UserHasAcl(AclTypes.aclBackofficeManualTasksCreate) ? (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            navigate(routePaths.settingsManualTasksTemplateConfig.path + "/" + "new");
                        }}
                    >
                        {t("button:newTemplate")}
                    </Button>
                ) : (
                    <></>
                );
            case "Services":
                return (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            if (!openModal) return;

                            openModal<ModalServiceManagerElementProps>(ModalServiceManager, {
                                context: "Create",
                                onConfirm: () => {
                                    setTrigger((prevState) => !prevState);
                                },
                            });
                        }}
                    >
                        {t("button:newService")}
                    </Button>
                );
            case "Tasks":
                return (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            if (!openModal) return;

                            openModal<ModalTaskManagerElementProps>(ModalTaskManager, {
                                context: "Create",
                                services: services,
                            });
                        }}
                    >
                        {t("button:newTask")}
                    </Button>
                );
        }
    }, [selectedConfig]);

    return (
        <>
            <Grid2 container spacing={2} pt={2}>
                <Grid2 size={4}>
                    <PageFilter
                        variant="warning"
                        title={templates.length}
                        subtitle={t("page:pageFilter.subTitle.templates")}
                        icon={<WidgetsOutlinedIcon />}
                        active={selectedConfig === "Templates"}
                        onClick={() => setSelectedConfig("Templates")}
                        isLoading={isLoading}
                    />
                </Grid2>

                <Grid2 size={4}>
                    <PageFilter
                        variant="primary"
                        title={services.length}
                        subtitle={t("common:services")}
                        icon={<CallSplitOutlinedIcon />}
                        active={selectedConfig === "Services"}
                        onClick={() => setSelectedConfig("Services")}
                        isLoading={isLoading}
                    />
                </Grid2>

                <Grid2 size={4}>
                    <PageFilter
                        variant="info"
                        title={tasks.length}
                        subtitle={t("common:tasks")}
                        icon={<EmojiPeopleOutlinedIcon />}
                        active={selectedConfig === "Tasks"}
                        onClick={() => setSelectedConfig("Tasks")}
                        isLoading={isLoading}
                    />
                </Grid2>
            </Grid2>
            {selectedConfig === "Templates" && (
                <>
                    <ListPageHeader>
                        <ListPageHeaderLeft>
                            <Stack>
                                <Typography variant="h5">{t("page:body.templatesDefinitions")}</Typography>
                                <Typography variant="body2" color="text.tier">
                                    {t("page:body.manageTemplatesAsManualTasksWorkflowOrchestration")}
                                </Typography>
                            </Stack>
                        </ListPageHeaderLeft>
                    </ListPageHeader>
                    <DataTable
                        loading={isLoading}
                        tableKey={TableUuids.SETTINGS_MANUAL_TASKS_TEMPLATES}
                        rows={
                            templates
                        }
                        onRowClick={(data) => {
                            if (!((data?.row?.isInternal && !UserHasAcl(AclTypes.aclSuperUser)) || data?.row?.isSystem))
                                handleConfigTemplates(data?.row?.id);
                        }}
                        disableRowSelectionOnClick={true}
                        additionalActions={additionalActions}
                        columnHeaderHeight={48}
                        rowHeight={56}
                        getRowHeight={() => "auto"}
                        columns={templatesColumns}
                    />
                </>
            )}
            {selectedConfig === "Services" && (
                <>
                    <ListPageHeader>
                        <ListPageHeaderLeft>
                            <Stack>
                                <Typography variant="h5">{t("page:body.servicesDefinitions")}</Typography>
                                <Typography variant="body2" color="text.tier">
                                    {t("page:body.servicesDefinitionsInfo")}
                                </Typography>
                            </Stack>
                        </ListPageHeaderLeft>
                    </ListPageHeader>
                    <DataTable
                        tableKey={TableUuids.SETTINGS_MANUAL_TASKS_SERVICES}
                        loading={isLoading}
                        rows={services}
                        onRowClick={(data) => {
                            if (data?.row?.erpCode === null) {
                                if (!openModal) return;

                                openModal<ModalServiceManagerElementProps>(ModalServiceManager, {
                                    service: data.row,
                                    context: "Update",
                                    onConfirm: () => {
                                        setTrigger((prevState) => !prevState);
                                    },
                                });
                            }
                        }}
                        disableRowSelectionOnClick={true}
                        additionalActions={additionalActions}
                        columnHeaderHeight={48}
                        rowHeight={56}
                        getRowHeight={() => "auto"}
                        columns={servicesColumns}
                    />
                </>
            )}
            {selectedConfig === "Tasks" && (
                <>
                    <ListPageHeader>
                        <ListPageHeaderLeft>
                            <Stack>
                                <Typography variant="h5">{t("page:body.tasksDefinitions")}</Typography>
                                <Typography variant="body2" color="text.tier">
                                    {t("page:body.tasksDefinitionsInfo")}
                                </Typography>
                            </Stack>
                        </ListPageHeaderLeft>
                    </ListPageHeader>
                    <DataTable
                        loading={isLoading}
                        tableKey={TableUuids.SETTINGS_MANUAL_TASKS_TASKS}
                        rows={tasks}
                        onRowClick={(data) => {
                            if (data?.row?.erpTaskCode === null || UserHasAcl(AclTypes.aclSuperUser)) {
                                if (!openModal) return;

                                openModal<ModalTaskManagerElementProps>(ModalTaskManager, {
                                    task: data.row,
                                    context: "Update",
                                    services: services,
                                });
                            }
                        }}
                        disableRowSelectionOnClick={true}
                        additionalActions={additionalActions}
                        columnHeaderHeight={48}
                        rowHeight={56}
                        getRowHeight={() => "auto"}
                        columns={tasksColumns}
                    />
                </>
            )}
        </>
    );
}

export default AdminSettingsManualTasks;
